import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MenuEditorDialog = ({ open, onOpenChange }) => {
  const [menu, setMenu] = useState([]);
  const [adding, setAdding] = useState({});
  const [newCat, setNewCat] = useState("");

  const load = () => axios.get(`${API}/menu`).then((r) => setMenu(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const patch = async (id, field, value) => {
    try { await axios.patch(`${API}/menu/items/${id}`, { [field]: field === "price" ? Number(value) : value }); }
    catch { toast.error("Could not save change"); }
  };
  const del = async (id) => { try { await axios.delete(`${API}/menu/items/${id}`); toast.success("Item removed"); load(); } catch { toast.error("Could not delete"); } };
  const add = async (cat) => {
    const a = adding[cat] || {};
    if (!a.name) return;
    try {
      await axios.post(`${API}/menu/items`, { category: cat, name: a.name, price: Number(a.price || 0) });
      setAdding((s) => ({ ...s, [cat]: { name: "", price: "" } }));
      load(); toast.success("Item added");
    } catch { toast.error("Could not add item"); }
  };
  const renameCat = async (oldName, newName) => {
    const nn = (newName || "").trim();
    if (!nn || nn === oldName) return;
    try { await axios.patch(`${API}/menu/categories`, { old_name: oldName, new_name: nn }); load(); toast.success("Category renamed"); }
    catch { toast.error("Could not rename category"); load(); }
  };
  const addCat = async () => {
    const nn = newCat.trim();
    if (!nn) return;
    try { await axios.post(`${API}/menu/categories`, { name: nn }); setNewCat(""); load(); toast.success("Category added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add category"); }
  };
  const delCat = async (name) => {
    try { await axios.delete(`${API}/menu/categories/${encodeURIComponent(name)}`); load(); toast.success("Category removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove category"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="menu-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-wide">Edit menu</DialogTitle>
          <DialogDescription>Change names & prices, remove items, or add new ones. Saved automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {menu.map((cat) => (
            <div key={cat.category}>
              <div className="mb-1.5 flex items-center gap-1">
                <input defaultValue={cat.category} data-testid={`cat-name-${cat.category}`}
                  onBlur={(e) => renameCat(cat.category, e.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-[11px] font-semibold uppercase tracking-wider text-brand-700 outline-none focus:border-b focus:border-brand" />
                {cat.items.length === 0 && (
                  <button data-testid={`del-category-${cat.category}`} onClick={() => delCat(cat.category)}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-destructive hover:bg-destructive/10"><Trash2 size={13} /></button>
                )}
              </div>
              <div className="space-y-1.5">
                {cat.items.map((it) => (
                  <div key={it.id} data-testid={`edit-item-${it.id}`} className="flex items-center gap-2">
                    <input defaultValue={it.name} onBlur={(e) => e.target.value !== it.name && patch(it.id, "name", e.target.value)}
                      className="h-9 min-w-0 flex-1 rounded-lg border border-sand-300 bg-white px-2 text-sm outline-none focus:border-brand" />
                    <input defaultValue={it.price} onBlur={(e) => Number(e.target.value) !== it.price && patch(it.id, "price", e.target.value)}
                      className="h-9 w-16 rounded-lg border border-sand-300 bg-white px-2 text-sm outline-none focus:border-brand" />
                    <button data-testid={`del-item-${it.id}`} onClick={() => del(it.id)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={15} /></button>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-0.5">
                  <input placeholder="New item name" value={adding[cat.category]?.name || ""}
                    onChange={(e) => setAdding((s) => ({ ...s, [cat.category]: { ...s[cat.category], name: e.target.value } }))}
                    className="h-9 min-w-0 flex-1 rounded-lg border border-dashed border-sand-400 bg-sand-50 px-2 text-sm outline-none focus:border-brand" />
                  <input placeholder="€" value={adding[cat.category]?.price || ""}
                    onChange={(e) => setAdding((s) => ({ ...s, [cat.category]: { ...s[cat.category], price: e.target.value.replace(/[^\d.]/g, "") } }))}
                    className="h-9 w-16 rounded-lg border border-dashed border-sand-400 bg-sand-50 px-2 text-sm outline-none focus:border-brand" />
                  <button data-testid={`add-item-${cat.category}`} onClick={() => add(cat.category)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand text-white hover:bg-brand-600"><Plus size={16} /></button>
                </div>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 border-t border-sand-300 pt-4">
            <input placeholder="New category name" value={newCat} data-testid="new-category-input"
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCat()}
              className="h-9 min-w-0 flex-1 rounded-lg border border-dashed border-brand/50 bg-white px-2 text-sm outline-none focus:border-brand" />
            <button data-testid="add-category-btn" onClick={addCat}
              className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-600"><Plus size={15} /> Category</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
